import currency from "currency.js";
import { forwardRef } from "react";
import { OptionTypeResponse, SideResponse } from "../../../codegen-api";
import { COLORS } from "../../../constants/design/colors";
import { formatSizePrecision } from "../../../utils/format";
import { Cell, OptionRow, OptionsRowWrapper } from "../style";
import { StrikeColumn } from "../../shared/Table/style";
import BidAskSizeCell from "./Cells/BidAskSizeCell";
import PositionCell from "./Cells/PositionCell";
import { useOptionsChainTableHeaders } from "./headers";
import { IOptionVolume, ITableContentRowWithCallPut } from "./types";

interface INeedsAnimation {
  call: {
    bid: boolean;
    ask: boolean;
  };
  put: {
    bid: boolean;
    ask: boolean;
  };
}

interface IRowSingleProps {
  row: ITableContentRowWithCallPut;
  onTrade: (
    orderType: OptionTypeResponse,
    expiry: number,
    strike: string
  ) => void;
  totalCallsPutsSize: IOptionVolume;
  sideTableWidth: string;
  strikeWidth: number;
  decimals: number;
  mode: OptionTypeResponse;
  activeCellType: OptionTypeResponse | undefined;
  needsAnimation: INeedsAnimation;
  computeIV: (type: OptionTypeResponse, side: SideResponse) => string;
  bidHeaders: ReturnType<typeof useOptionsChainTableHeaders>["bidHeaders"];
  headers: ReturnType<typeof useOptionsChainTableHeaders>["headers"];
}

const RowSingle = forwardRef<any, IRowSingleProps>(
  (
    {
      row,
      onTrade,
      totalCallsPutsSize,
      sideTableWidth,
      strikeWidth,
      decimals,
      mode,
      activeCellType,
      needsAnimation,
      computeIV,
      bidHeaders,
      headers,
    },
    ref
  ) => {
    const rowInfo = row[mode];
    const needsAnimationMode = needsAnimation[mode];
    const totalVolumeMode =
      mode === "call" ? totalCallsPutsSize.calls : totalCallsPutsSize.puts;

    return (
      <OptionsRowWrapper ref={ref}>
        <>
          <StrikeColumn
            $width={strikeWidth}
            activeBorderSide={activeCellType && "left"}
          >
            <span>
              {row.strike
                ? currency(row.strike, { precision: 0 }).format()
                : "-"}
            </span>
          </StrikeColumn>
          <OptionRow
            disabled={!rowInfo.instrumentName}
            $width={sideTableWidth}
            onClick={
              rowInfo.instrumentName
                ? () => onTrade(mode, row.expiry, row.strike)
                : undefined
            }
            $isActive={!!activeCellType}
            side={"right"}
          >
            {headers.position && (
              <PositionCell
                align={headers.position?.align}
                position={rowInfo.position}
                side={rowInfo.side}
              />
            )}
            <BidAskSizeCell
              orderVolume={rowInfo.bid.orderVolume}
              totalVolume={totalVolumeMode.bids}
              volume={Number(rowInfo.bid.volume)}
              needsAnimation={needsAnimationMode.bid}
              side={SideResponse.Buy}
              align={bidHeaders.bidSize.align}
            />
            {bidHeaders.priceIvBid && (
              <Cell dim align={bidHeaders.priceIvBid.align}>
                <Cell
                  style={{ color: COLORS.up.one }}
                  align={bidHeaders.priceIvBid.align}
                >
                  {rowInfo.bid.price && Number(rowInfo.bid.price)
                    ? currency(rowInfo.bid.price).format()
                    : "-"}
                </Cell>
                {computeIV(mode, SideResponse.Buy)}
              </Cell>
            )}
            {bidHeaders.markPrice && (
              <Cell>
                <Cell align={bidHeaders.markPrice.align}>
                  {Number(rowInfo.markPrice) > 0.01
                    ? currency(Number(rowInfo.markPrice)).format({
                        precision: 2,
                      })
                    : "-"}
                </Cell>
                <Cell dim $fontSize="small" align={bidHeaders.markPrice.align}>
                  {Number(rowInfo.markPrice) > 0.01
                    ? `${(Number(rowInfo.iv) * 100).toFixed(2)}%`
                    : "-"}
                </Cell>
              </Cell>
            )}
            {bidHeaders.priceIvAsk && (
              <Cell dim align={bidHeaders.priceIvAsk.align}>
                <Cell
                  style={{ color: COLORS.down.one }}
                  align={bidHeaders.priceIvAsk.align}
                >
                  {rowInfo.ask.price && Number(rowInfo.ask.price)
                    ? currency(rowInfo.ask.price).format()
                    : "-"}
                </Cell>
                {computeIV(mode, SideResponse.Sell)}
              </Cell>
            )}
            <BidAskSizeCell
              orderVolume={rowInfo.ask.orderVolume}
              totalVolume={totalVolumeMode.asks}
              volume={Number(rowInfo.ask.volume)}
              needsAnimation={needsAnimationMode.ask}
              side={SideResponse.Sell}
              align={bidHeaders.askSize.align}
            />
            {headers.delta && (
              <Cell dim align={headers.delta.align}>
                {Number(rowInfo.delta) !== 0 &&
                  Number(rowInfo.delta).toFixed(2)}
              </Cell>
            )}
            {headers.oi && (
              <Cell dim align={headers.oi.align}>
                {Number(rowInfo.open_interest)
                  ? formatSizePrecision(
                      Number(rowInfo.open_interest || 0),
                      decimals
                    )
                  : "-"}
              </Cell>
            )}
          </OptionRow>
        </>
      </OptionsRowWrapper>
    );
  }
);

export default RowSingle;
